import React, { useEffect, useMemo, useState, useRef, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTenant } from '../../../theme/tokens';
import { PlusIcon, SearchIcon, QrCodeIcon, CheckCircleIcon, PauseCircleIcon, ScanLineIcon, MoreHorizontalIcon, EyeIcon, CopyIcon, DownloadIcon, PauseIcon, PlayIcon, TrashIcon, XIcon } from 'lucide-react';
type QRStatus = 'active' | 'paused' | 'expired';
interface QRCodeEntry {
  id: string;
  serial: string;
  label: string;
  location: string;
  status: QRStatus;
  totalScans: number;
  scansLast30d: number;
  totalCollected: number; // in cents
  createdAt: string; // ISO date
  lastScanAt: string | null;
}
const PRIMARY = '#0F4C9C';
// Deterministic decorative QR (purely visual)
function decorativeQR(seed: string, size = 200) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = h * 31 + seed.charCodeAt(i) | 0;
  const grid = 29;
  const cells: boolean[] = [];
  for (let i = 0; i < grid * grid; i++) {
    h = h * 1103515245 + 12345 & 0x7fffffff;
    cells.push(h % 100 < 48);
  }
  const cell = size / grid;
  const rects: string[] = [];
  for (let y = 0; y < grid; y++) {
    for (let x = 0; x < grid; x++) {
      const inTopLeft = x < 8 && y < 8;
      const inTopRight = x >= grid - 8 && y < 8;
      const inBottomLeft = x < 8 && y >= grid - 8;
      if (inTopLeft || inTopRight || inBottomLeft) continue;
      if (cells[y * grid + x]) {
        rects.push(`<rect x="${x * cell}" y="${y * cell}" width="${cell}" height="${cell}" fill="#fff" />`);
      }
    }
  }
  const finder = (fx: number, fy: number) => `
    <rect x="${fx * cell}" y="${fy * cell}" width="${cell * 7}" height="${cell * 7}" fill="#fff"/>
    <rect x="${(fx + 1) * cell}" y="${(fy + 1) * cell}" width="${cell * 5}" height="${cell * 5}" fill="${PRIMARY}"/>
    <rect x="${(fx + 2) * cell}" y="${(fy + 2) * cell}" width="${cell * 3}" height="${cell * 3}" fill="#fff"/>
  `;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
    <rect width="${size}" height="${size}" fill="${PRIMARY}"/>
    ${rects.join('')}
    ${finder(0, 0)}
    ${finder(grid - 7, 0)}
    ${finder(0, grid - 7)}
  </svg>`;
}
export function QRCodes() {
  const navigate = useNavigate();
  const currency = getTenant().currency.code;
  const [qrCodes, setQrCodes] = useState<QRCodeEntry[]>([{
    id: 'qr-1',
    serial: 'JWPH63S4WX',
    label: 'Main terminal',
    location: 'BML Experience Zone',
    status: 'active',
    totalScans: 1245,
    scansLast30d: 312,
    totalCollected: 4520050,
    createdAt: '2025-11-12',
    lastScanAt: '2026-05-18'
  }, {
    id: 'qr-2',
    serial: 'JTKINHTYZ7',
    label: 'Counter 2',
    location: 'BML Experience Zone',
    status: 'active',
    totalScans: 873,
    scansLast30d: 198,
    totalCollected: 2890075,
    createdAt: '2025-12-04',
    lastScanAt: '2026-05-19'
  }, {
    id: 'qr-3',
    serial: 'KZQ4HX9P2L',
    label: 'Pop-up booth',
    location: 'Subang Jaya branch',
    status: 'paused',
    totalScans: 412,
    scansLast30d: 0,
    totalCollected: 845200,
    createdAt: '2026-01-18',
    lastScanAt: '2026-04-02'
  }, {
    id: 'qr-4',
    serial: 'M3RT8YA1NX',
    label: 'Ramadan promo',
    location: 'Main branch lobby',
    status: 'expired',
    totalScans: 2104,
    scansLast30d: 0,
    totalCollected: 7654000,
    createdAt: '2025-09-01',
    lastScanAt: '2026-03-15'
  }]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | QRStatus>('all');
  const [contextMenuOpenId, setContextMenuOpenId] = useState<string | null>(null);
  const [zoomTarget, setZoomTarget] = useState<QRCodeEntry | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        setContextMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const filtered = useMemo(() => {
    return qrCodes.filter(qr => {
      const matchesSearch = !searchQuery || qr.label.toLowerCase().includes(searchQuery.toLowerCase()) || qr.serial.toLowerCase().includes(searchQuery.toLowerCase()) || qr.location.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = activeTab === 'all' || qr.status === activeTab;
      return matchesSearch && matchesStatus;
    });
  }, [qrCodes, searchQuery, activeTab]);
  const totalQR = qrCodes.length;
  const activeQR = qrCodes.filter(q => q.status === 'active').length;
  const totalScans30d = qrCodes.reduce((sum, q) => sum + q.scansLast30d, 0);
  const totalCollected30dMinor = qrCodes.filter(q => q.status !== 'expired').reduce((sum, q) => sum + q.totalCollected, 0);
  const formatMoney = (minor: number) => (minor / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2
  });
  const formatDate = (d: string | null) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB');
  };
  const getStatusBadge = (status: QRStatus) => {
    const styles: Record<QRStatus, string> = {
      active: 'bg-green-100 text-green-800',
      paused: 'bg-amber-100 text-amber-800',
      expired: 'bg-gray-100 text-gray-600'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${styles[status]}`} data-id="element-8432">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  const handleCopy = (qr: QRCodeEntry) => {
    navigator.clipboard?.writeText(`https://pay.bilabila.co/q/${qr.serial}`).catch(() => {});
    setCopiedId(qr.id);
    setTimeout(() => setCopiedId(null), 1500);
  };
  const handleDownload = (qr: QRCodeEntry) => {
    const svg = decorativeQR(qr.serial, 512);
    const blob = new Blob([svg], {
      type: 'image/svg+xml'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `paymv-qr-${qr.serial.toLowerCase()}.svg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const toggleStatus = (id: string) => {
    setQrCodes(codes => codes.map(qr => qr.id === id ? {
      ...qr,
      status: qr.status === 'active' ? 'paused' : qr.status === 'paused' ? 'active' : qr.status
    } : qr));
  };
  const handleDelete = (id: string) => {
    setQrCodes(codes => codes.filter(qr => qr.id !== id));
  };
  return <div className="max-w-[1400px] mx-auto space-y-8" data-id="element-8433">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-8434">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-8435">QR codes</h1>
        <div className="flex items-center gap-3" data-id="element-8436">
          <button onClick={() => {
          /* future: open create flow */
        }} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-8437">
            Create QR code
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-8438">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-8439">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-8440">
            <QrCodeIcon size={16} className="text-gray-500" data-id="element-8441" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-8442">
              Total QR codes
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-8443">
            <div className="text-2xl font-bold text-gray-900" data-id="element-8444">{totalQR}</div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-8445">
            All time
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-8446">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-8447">
            <CheckCircleIcon size={16} className="text-green-500" data-id="element-8448" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-8449">Active</span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-8450">
            <div className="text-2xl font-bold text-green-600" data-id="element-8451">{activeQR}</div>
          </div>
          <div className="px-4 py-2 bg-green-50 text-xs text-green-700 font-medium" data-id="element-8452">
            Available for scanning
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-8453">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-8454">
            <ScanLineIcon size={16} className="text-gray-500" data-id="element-8455" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-8456">
              Scans (30d)
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-8457">
            <div className="text-2xl font-bold text-gray-900" data-id="element-8458">
              {totalScans30d.toLocaleString('en-US')}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-8459">
            Across {activeQR} active codes
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-8460">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-8461">
            <PauseCircleIcon size={16} className="text-amber-500" data-id="element-8462" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-8463">
              Collected
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-8464">
            <div className="text-2xl font-bold text-gray-900" data-id="element-8465">
              {currency} {formatMoney(totalCollected30dMinor)}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-8466">
            Lifetime via QR
          </div>
        </div>
      </div>

      <div className="space-y-6" data-id="element-8467">
        {/* Tabs + Search */}
        <div className="flex items-end justify-between border-b border-gray-200" data-id="element-8468">
          <div className="flex space-x-6" role="tablist" aria-label="QR codes" data-id="element-8469">
            {[{
            id: 'all',
            label: 'All'
          }, {
            id: 'active',
            label: 'Active'
          }, {
            id: 'paused',
            label: 'Paused'
          }, {
            id: 'expired',
            label: 'Expired'
          }].map(tab => <button key={tab.id} role="tab" aria-selected={activeTab === tab.id} onClick={() => setActiveTab(tab.id as 'all' | QRStatus)} className={`pb-4 -mb-px text-sm font-medium transition-colors border-b-2 ${activeTab === tab.id ? 'border-[#E2231A]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-8470">
                {tab.label}
              </button>)}
          </div>
          <div className="flex items-center gap-4 pb-3" data-id="element-8471">
            <div className="relative" data-id="element-8472">
              <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-8473" />
              <input type="search" aria-label="Search QR codes" placeholder="Search QR codes..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-56 pl-9 pr-4 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-8474" />
            </div>
          </div>
        </div>

        {/* Table list */}
        {filtered.length === 0 ? <div className="text-center py-12 bg-white rounded-xl border border-gray-200" data-id="element-8475">
            <p className="text-gray-500 text-sm" data-id="element-8476">
              No QR codes match your filters.
            </p>
          </div> : <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-8477">
            <table className="w-full text-left border-collapse" data-id="element-8478">
              <tbody className="divide-y divide-gray-100" data-id="element-8479">
                {filtered.map(qr => <tr key={qr.id} className="hover:bg-gray-50 transition-colors" data-id="element-8480">
                    <td className="px-5 py-4" data-id="element-8481">
                      <button type="button" onClick={() => setZoomTarget(qr)} className="w-10 h-10 rounded-md border border-gray-200 overflow-hidden flex-shrink-0 hover:ring-2 hover:ring-[#E2231A]/30 transition" aria-label="View QR" dangerouslySetInnerHTML={{
                  __html: decorativeQR(qr.serial, 40)
                }} data-id="element-8482" />
                    </td>
                    <td className="px-2 py-4" data-id="element-8483">
                      <div className="text-sm font-semibold text-gray-900" data-id="element-8484">
                        {qr.label}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5 font-mono" data-id="element-8485">
                        {qr.serial}
                      </div>
                    </td>
                    <td className="px-4 py-4" data-id="element-8486">
                      <div className="text-sm text-gray-700" data-id="element-8487">{qr.location}</div>
                    </td>
                    <td className="px-4 py-4" data-id="element-8488">{getStatusBadge(qr.status)}</td>
                    <td className="px-4 py-4" data-id="element-8489">
                      <div className="text-sm font-medium text-gray-900 tabular-nums" data-id="element-8490">
                        {qr.totalScans.toLocaleString('en-US')}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5" data-id="element-8491">
                        {qr.scansLast30d.toLocaleString('en-US')} last 30d
                      </div>
                    </td>
                    <td className="px-4 py-4" data-id="element-8492">
                      <div className="text-sm font-medium text-gray-900 tabular-nums" data-id="element-8493">
                        {currency} {formatMoney(qr.totalCollected)}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right" data-id="element-8494">
                      <div className="text-sm text-gray-500" data-id="element-8495">
                        Last scan {formatDate(qr.lastScanAt)}
                      </div>
                    </td>
                    <td className="px-4 py-4 w-12 text-right relative" data-id="element-8496">
                      <div ref={contextMenuOpenId === qr.id ? contextMenuRef : null} data-id="element-8497">
                        <button className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors" onClick={e => {
                    e.stopPropagation();
                    setContextMenuOpenId(contextMenuOpenId === qr.id ? null : qr.id);
                  }} aria-label="QR code actions" data-id="element-8498">
                          <MoreHorizontalIcon size={18} aria-hidden="true" data-id="element-8499" />
                        </button>

                        {contextMenuOpenId === qr.id && <>
                            <div className="fixed inset-0 z-10" onClick={e => {
                      e.stopPropagation();
                      setContextMenuOpenId(null);
                    }} data-id="element-8500" />
                            <div className="absolute right-4 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-8501">
                              <button onClick={e => {
                        e.stopPropagation();
                        setContextMenuOpenId(null);
                        setZoomTarget(qr);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-8502">
                                <EyeIcon size={16} className="mr-3 text-gray-400" data-id="element-8503" />
                                View QR
                              </button>
                              <button onClick={e => {
                        e.stopPropagation();
                        setContextMenuOpenId(null);
                        handleCopy(qr);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-8504">
                                <CopyIcon size={16} className="mr-3 text-gray-400" data-id="element-8505" />
                                {copiedId === qr.id ? 'Copied!' : 'Copy link'}
                              </button>
                              <button onClick={e => {
                        e.stopPropagation();
                        setContextMenuOpenId(null);
                        handleDownload(qr);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-8506">
                                <DownloadIcon size={16} className="mr-3 text-gray-400" data-id="element-8507" />
                                Download QR
                              </button>
                              {qr.status !== 'expired' && <button onClick={e => {
                        e.stopPropagation();
                        setContextMenuOpenId(null);
                        toggleStatus(qr.id);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-8508">
                                  {qr.status === 'active' ? <>
                                      <PauseIcon size={16} className="mr-3 text-gray-400" data-id="element-8509" />
                                      Pause
                                    </> : <>
                                      <PlayIcon size={16} className="mr-3 text-gray-400" data-id="element-8510" />
                                      Resume
                                    </>}
                                </button>}
                              <div className="border-t border-gray-100 my-1" data-id="element-8511" />
                              <button onClick={e => {
                        e.stopPropagation();
                        setContextMenuOpenId(null);
                        handleDelete(qr.id);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-8512">
                                <TrashIcon size={16} className="mr-3" data-id="element-8513" />
                                Delete
                              </button>
                            </div>
                          </>}
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>

      {/* Zoom modal */}
      {zoomTarget && <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setZoomTarget(null)} data-id="element-8514">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-8515">
            <div className="flex justify-between items-center px-5 py-4 border-b border-gray-200" data-id="element-8516">
              <div data-id="element-8517">
                <h3 className="text-base font-semibold text-gray-900" data-id="element-8518">
                  {zoomTarget.label}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5 font-mono" data-id="element-8519">
                  {zoomTarget.serial}
                </p>
              </div>
              <button type="button" onClick={() => setZoomTarget(null)} aria-label="Close" className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-8520">
                <XIcon size={18} data-id="element-8521" />
              </button>
            </div>
            <div className="p-6 flex flex-col items-center" data-id="element-8522">
              <div className="rounded-xl overflow-hidden p-3 bg-white border border-gray-200" dangerouslySetInnerHTML={{
            __html: decorativeQR(zoomTarget.serial, 240)
          }} data-id="element-8523" />
              <p className="mt-4 text-sm text-gray-500 text-center" data-id="element-8524">
                {zoomTarget.location}
              </p>
              <div className="mt-5 flex gap-2 w-full" data-id="element-8525">
                <button type="button" onClick={() => handleCopy(zoomTarget)} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-8526">
                  <CopyIcon size={14} data-id="element-8527" />
                  {copiedId === zoomTarget.id ? 'Copied!' : 'Copy link'}
                </button>
                <button type="button" onClick={() => handleDownload(zoomTarget)} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#E2231A] hover:bg-[#C41B14] rounded-lg shadow-sm transition-colors" data-id="element-8528">
                  <DownloadIcon size={14} data-id="element-8529" />
                  Download
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}